// ============================================================
// MUI Component Overrides
// ============================================================

import { tokens } from './tokens';

export const components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: tokens.colors.background,
        color: tokens.colors.text.primary,
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.button,
        padding: '8px 20px',
        fontWeight: tokens.typography.fontWeights.semiBold,
        transition: `all ${tokens.transitions.fast}`,
      },
      sizeSmall: {
        padding: '6px 14px',
        fontSize: tokens.typography.sizes.sm,
      },
      sizeLarge: {
        padding: '12px 28px',
        fontSize: tokens.typography.sizes.md,
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: tokens.colors.primary.dark,
        },
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
        },
      },
    },
  },

  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.card,
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: tokens.shadows.card,
        transition: `box-shadow ${tokens.transitions.normal}`,
      },
    },
  },

  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        backgroundImage: 'none',
      },
      outlined: {
        borderColor: tokens.colors.border,
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: 0,
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      size: 'small',
      variant: 'outlined',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: tokens.radius.input,
          '& fieldset': {
            borderColor: tokens.colors.border,
            transition: `border-color ${tokens.transitions.fast}`,
          },
          '&:hover fieldset': {
            borderColor: tokens.colors.primary.main,
          },
          '&.Mui-focused fieldset': {
            borderWidth: '1.5px',
          },
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.input,
        '& fieldset': {
          borderColor: tokens.colors.border,
        },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.xl,
        boxShadow: tokens.shadows.dialog,
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: `1px solid ${tokens.colors.border}`,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.chip,
        fontWeight: tokens.typography.fontWeights.medium,
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: tokens.typography.fontWeights.semiBold,
        fontSize: tokens.typography.sizes.body,
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        borderRadius: tokens.radius.sm,
        fontSize: tokens.typography.sizes.xs,
        fontWeight: tokens.typography.fontWeights.medium,
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.md,
        boxShadow: tokens.shadows.dropdown,
        border: `1px solid ${tokens.colors.border}`,
        marginTop: '4px',
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        margin: '2px 6px',
        padding: '8px 12px',
        fontSize: tokens.typography.sizes.body,
        '&.Mui-selected': {
          backgroundColor: tokens.colors.primary[50],
          '&:hover': {
            backgroundColor: tokens.colors.primary[100],
          },
        },
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        '&.Mui-selected': {
          backgroundColor: tokens.colors.primary[50],
          color: tokens.colors.primary.main,
          '&:hover': {
            backgroundColor: tokens.colors.primary[100],
          },
        },
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: tokens.colors.border,
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${tokens.colors.border}`,
        padding: '12px 16px',
        fontSize: tokens.typography.sizes.body,
      },
      head: {
        fontWeight: tokens.typography.fontWeights.semiBold,
        backgroundColor: tokens.colors.grey[50],
        color: tokens.colors.text.secondary,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.md,
      },
    },
  },

  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        fontSize: tokens.typography.sizes.body,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: tokens.typography.fontWeights.medium,
        fontSize: tokens.typography.sizes.body,
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.full,
        height: 6,
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.md,
        boxShadow: tokens.shadows.dropdown,
        border: `1px solid ${tokens.colors.border}`,
        marginTop: '4px',
      },
      option: {
        borderRadius: tokens.radius.sm,
        margin: '2px 6px',
        padding: '8px 12px',
        fontSize: tokens.typography.sizes.body,
        '&[aria-selected="true"]': {
          backgroundColor: tokens.colors.primary[50],
          '&:hover': {
            backgroundColor: tokens.colors.primary[100],
          },
        },
      },
    },
  },
};
